/**
 * Mixpanel analytics wrapper.
 *
 * Principles:
 * - Never throw. Never crash the app.
 * - Phone number is the distinct_id (must match backend).
 * - Super Admin (9718020020) and dev/test accounts are opt-out.
 * - Safe to call before mixpanel.init completes — stubs silently.
 */
import mixpanel from 'mixpanel-browser';
import { captureException as sentryCapture, SENTRY_ENABLED } from '../sentry';

const TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';
const SUPER_ADMIN_PHONE = '9718020020';
const TEST_PHONES = new Set([
  '1234567890', '5555555555', '7878787878',
  '7070707070', '8888888888', '9090909090', '1010101010', '4040404040',
]);

// PRODUCTION-ONLY: we only want real user behaviour in the dashboard. Anything
// that isn't the live production host is considered dev/preview and tracking
// is disabled. Override with `window.__mp_force_enable__ = true` in DevTools
// for a one-off smoke test.
const PROD_HOSTS = new Set(['rentkollect.com', 'www.rentkollect.com']);
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  if (window.__mp_force_enable__) return true;
  return PROD_HOSTS.has(window.location.hostname);
};

let _initialized = false;
let _currentPhone = null;
let _optedOut = false;

/**
 * Centralized error swallower for analytics. Analytics must never crash the
 * app or spam users' consoles in production. In dev/preview we log via
 * console.debug so issues can be found while iterating; in prod we route to
 * Sentry (if a DSN is configured) so silent failures stay visible.
 */
const _swallow = (where, err) => {
  if (process.env.NODE_ENV !== 'production') {
    try { console.debug(`[analytics:${where}]`, err?.message || err); } catch (_) { /* nothing more we can do */ }
  }
  if (SENTRY_ENABLED && err) {
    try { sentryCapture(err, { tags: { where: `analytics:${where}` } }); } catch (_) { /* noop */ }
  }
};

export const initMixpanel = () => {
  if (_initialized || !TOKEN) return;
  if (!isProduction()) return;  // dev/preview: don't load the client at all
  try {
    mixpanel.init(TOKEN, {
      // Heatmaps & autocapture: Mixpanel needs element-level capture turned on.
      // This sends $click / $form_submit / $input_blur events with CSS selectors
      // so Mixpanel's heatmap UI can paint hot zones on top of the live site.
      autocapture: {
        pageview: false,        // we send our own named page events below
        click:    true,         // required for click heatmaps
        input:    true,
        scroll:   true,
        submit:   true,
      },
      record_sessions_percent: 0,   // disable session replay (separate billing)
      persistence: 'localStorage',
      debug: false,
      track_pageview: false,    // We track pageviews manually via router
      ignore_dnt: false,
    });
    // App context super-props (attached to every event)
    mixpanel.register({
      app: 'rentkollect',
      platform: 'web',
      env: process.env.NODE_ENV,
    });
    _initialized = true;
  } catch (e) {
    _swallow('init', e);
  }
};

const _isOptOut = (phone) => {
  if (!phone) return false;
  if (phone === SUPER_ADMIN_PHONE) return true;
  if (TEST_PHONES.has(phone)) return true;
  return false;
};

/** Call on successful login / page refresh with persisted user. */
export const identifyUser = (user) => {
  if (!_initialized || !user?.phone) return;
  try {
    _currentPhone = user.phone;
    _optedOut = _isOptOut(user.phone);
    if (_optedOut) return;

    mixpanel.identify(user.phone);
    mixpanel.people.set({
      $name: user.name || `Manager ${user.phone.slice(-4)}`,
      $phone: user.phone,
      role: user.role || 'manager',
      last_seen_web: new Date().toISOString(),
    });
    mixpanel.register({
      user_role: user.role || 'manager',
    });
  } catch (e) { _swallow('analytics', e); }
};

/** Call on logout — prevents event bleed across users on shared devices. */
export const resetMixpanel = () => {
  if (!_initialized) return;
  try {
    mixpanel.reset();
    _currentPhone = null;
    _optedOut = false;
  } catch (e) { _swallow('analytics', e); }
};

/** Fire a custom event. */
export const track = (event, properties = {}) => {
  if (!_initialized || _optedOut) return;
  try {
    mixpanel.track(event, properties);
  } catch (e) { _swallow('analytics', e); }
};

// Map a URL pathname → human-readable page name. Order matters (longest match first).
// Whatever doesn't match falls back to a Title-cased path segment.
const PAGE_TITLES = [
  // Manager app
  [/^\/manager\/home/,                 'Home Dashboard'],
  [/^\/manager\/money\/collection/,    'Collections'],
  [/^\/manager\/money\/expenses/,      'Expenses'],
  [/^\/manager\/money\/dues-packages/, 'Dues Packages'],
  [/^\/manager\/money\/dues/,          'Dues'],
  [/^\/manager\/money\/banks/,         'Banks & UPI'],
  [/^\/manager\/money\/profit-loss/,   'Profit & Loss'],
  [/^\/manager\/money/,                'Money Overview'],
  [/^\/manager\/property\/list/,       'Properties'],
  [/^\/manager\/properties\/[^/]+\/edit/, 'Edit Property'],
  [/^\/manager\/properties\/[^/]+/,    'Property Details'],
  [/^\/manager\/rooms/,                'Rooms'],
  [/^\/manager\/people\/tenants\/[^/]+/, 'Tenant Profile'],
  [/^\/manager\/people\/tenants/,      'Tenants'],
  [/^\/manager\/people\/leads/,        'Leads'],
  [/^\/manager\/people\/bookings/,     'Bookings'],
  [/^\/manager\/people\/agreements/,   'Rent Agreements'],
  [/^\/manager\/operations\/complaints/, 'Complaints'],
  [/^\/manager\/operations\/tasks/,    'Tasks'],
  [/^\/manager\/operations\/food/,     'Food / Mess'],
  [/^\/manager\/operations\/cleaning/, 'Cleaning'],
  [/^\/manager\/operations\/assets/,   'Assets'],
  [/^\/manager\/operations\/visitors/, 'Visitor Log'],
  [/^\/manager\/operations/,           'Operations'],
  [/^\/manager\/listings/,             'Public Listings'],
  [/^\/manager\/settings/,             'Settings'],
  [/^\/manager\/wallet/,               'Wallet'],
  [/^\/manager\/login/,                'Manager Login'],
  [/^\/manager/,                       'Manager App'],
  // Admin
  [/^\/admin\/login/,                  'Admin Login'],
  [/^\/admin/,                         'Super Admin'],
  // Public
  [/^\/listings\/[^/]+/,               'Listing Detail'],
  [/^\/listings/,                      'Listings Index'],
  [/^\/tenant/,                        'Tenant Onboarding'],
  [/^\/$/,                             'Landing Page'],
];

const pageNameFor = (pathname) => {
  for (const [re, name] of PAGE_TITLES) {
    if (re.test(pathname)) return name;
  }
  // Fallback: title-case the last segment ("foo-bar" → "Foo Bar")
  const last = (pathname.replace(/\/$/, '').split('/').pop() || 'home')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return last || 'Untitled Page';
};

/**
 * Track a page view — call from a Router listener.
 *
 * Fires TWO events so Mixpanel reports are clean:
 *   1. `Page · <Name>`   — distinct event per page (great for Top Events report,
 *                           heatmaps, and per-page conversion funnels).
 *   2. `page_viewed`     — single event with `page_name` property (great for
 *                           breakdown/segmentation across all pages at once).
 */
export const trackPageView = (pathname, extras = {}) => {
  if (!_initialized || _optedOut) return;
  try {
    const pageName = pageNameFor(pathname);
    const props = { page_name: pageName, path: pathname, ...extras };
    // Distinct event per page — shows up as separate rows in Mixpanel's
    // "Events" list (e.g. `Page · Collections`, `Page · Dues`, …).
    mixpanel.track(`Page · ${pageName}`, props);
    // Aggregate event — useful when you want to segment ALL pageviews by name
    // in a single report without having to OR 30 event names.
    mixpanel.track('page_viewed', props);
    // Also register the current page as a super-property so every subsequent
    // event (clicks, form submits, custom track()) carries it automatically.
    mixpanel.register({ current_page: pageName });
  } catch (e) { _swallow('analytics', e); }
};

/** Simple time-on-event helpers. */
export const timeEvent = (event) => {
  if (!_initialized || _optedOut) return;
  try { mixpanel.time_event(event); } catch (e) { _swallow('analytics', e); }
};

/** Update people profile props. */
export const peopleSet = (props) => {
  if (!_initialized || _optedOut) return;
  try { mixpanel.people.set(props); } catch (e) { _swallow('analytics', e); }
};

/** Increment a numeric profile property (e.g. lifetime payments_recorded). */
export const peopleIncrement = (prop, by = 1) => {
  if (!_initialized || _optedOut) return;
  try { mixpanel.people.increment(prop, by); } catch (e) { _swallow('analytics', e); }
};

/** "Set once" — fires only if the prop is unset. Perfect for first_*_at timestamps. */
export const peopleSetOnce = (props) => {
  if (!_initialized || _optedOut) return;
  try { mixpanel.people.set_once(props); } catch (e) { _swallow('analytics', e); }
};

/**
 * Fire a "first time done" event exactly once per user-event combination.
 * Backed by localStorage so it survives reloads but resets on logout (via reset()).
 * Use cases: first_property_created, first_tenant_added, first_payment_recorded.
 *
 * @param {string} eventKey  — short slug e.g. 'first_payment_recorded'.
 * @param {object} props     — extra event props.
 */
export const trackFirstTime = (eventKey, props = {}) => {
  if (!_initialized || _optedOut || typeof window === 'undefined') return;
  try {
    const storageKey = `rk_first_${_currentPhone || 'anon'}_${eventKey}`;
    if (localStorage.getItem(storageKey)) return;  // already fired
    localStorage.setItem(storageKey, new Date().toISOString());
    mixpanel.track(eventKey, props);
    // Stamp on user profile so cohort/funnel reports can filter
    mixpanel.people.set_once({ [`${eventKey}_at`]: new Date().toISOString() });
  } catch (e) { _swallow('analytics', e); }
};

/**
 * Session duration tracker.
 * Call once after initMixpanel(). Fires `session_ended` with duration when the
 * tab is closed/refreshed.  Uses `visibilitychange` (more reliable than unload).
 */
let _sessionStartedAt = null;
let _sessionListenerBound = false;
export const startSessionTimer = () => {
  if (_sessionListenerBound || typeof window === 'undefined') return;
  _sessionStartedAt = Date.now();
  _sessionListenerBound = true;
  const flush = () => {
    if (!_initialized || _optedOut || !_sessionStartedAt) return;
    const seconds = Math.round((Date.now() - _sessionStartedAt) / 1000);
    if (seconds < 5) return;  // ignore noisy bounces
    try {
      mixpanel.track('session_ended', { duration_seconds: seconds, current_page: window.location.pathname });
      mixpanel.people.increment('lifetime_minutes', Math.round(seconds / 60));
    } catch (e) { _swallow('analytics', e); }
    _sessionStartedAt = Date.now();  // reset for next session
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
    if (document.visibilityState === 'visible' && !_sessionStartedAt) {
      _sessionStartedAt = Date.now();
    }
  });
  window.addEventListener('pagehide', flush);
};

/**
 * Bind an axios interceptor that fires `api_error_observed` for every 4xx/5xx
 * response. Cheapest way to surface broken flows in Mixpanel.
 * Call once at app boot AFTER axios is imported.
 */
export const bindAxiosErrorTracker = (axiosInstance) => {
  if (!axiosInstance || axiosInstance.__rk_error_bound) return;
  axiosInstance.__rk_error_bound = true;
  axiosInstance.interceptors.response.use(
    (resp) => resp,
    (err) => {
      try {
        if (!_initialized || _optedOut) return Promise.reject(err);
        const status = err?.response?.status || 0;
        if (status === 401) return Promise.reject(err);  // login expired, not a "bug"
        const url = err?.config?.url || '';
        const method = (err?.config?.method || 'get').toUpperCase();
        // Strip the host & query so endpoints aggregate cleanly in Mixpanel
        const endpoint = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
        mixpanel.track('api_error_observed', {
          status,
          method,
          endpoint,
          message: err?.response?.data?.detail || err?.message || 'unknown',
          current_page: typeof window !== 'undefined' ? window.location.pathname : '',
        });
      } catch (_) { _swallow('analytics', _); }
      return Promise.reject(err);
    },
  );
};

/**
 * Listen for cross-tab data-sync events fired from the BroadcastChannel and
 * push them to Mixpanel as `cross_tab_sync_fired`. Tells you how often
 * managers really do work in multiple tabs.
 */
export const bindCrossTabTracker = () => {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;
  try {
    const ch = new BroadcastChannel('rentkollect-sync');
    ch.onmessage = (msg) => {
      if (!_initialized || _optedOut) return;
      try { mixpanel.track('cross_tab_sync_fired', { event: msg.data?.event }); } catch (e) { _swallow('analytics', e); }
    };
  } catch (e) { _swallow('analytics', e); }
};
